import { PRIORITIES, TASK_URGENCIES } from "@handoverpro/shared";

import {
  discardPatientDraft as sdkDiscardPatientDraft,
  getPatientDraft as sdkGetPatientDraft,
  savePatientDraft as sdkSavePatientDraft,
} from "~/lib/api";
import { EMPTY_PATIENT_FORM, type PatientFormValues } from "~/session/patient-form/schema";

/**
 * Network layer for the patient-form auto-save draft (V2-E10.1, HAN-72). Keeps
 * the generated SDK and the draft ↔ form-values translation in one place so the
 * `<PatientForm>` and its screen stay free of API shape details.
 *
 * Auto-save is best-effort: every call swallows its errors and returns a simple
 * success boolean (or null), because a failed draft save must never interrupt
 * the doctor — the real Save is the source of truth. The `patientId` argument
 * discriminates the two drafts: `undefined` is the create-mode (new patient)
 * draft, a value is the edit-mode draft for that patient.
 */

/**
 * Coerce an opaque stored draft back into form values. The draft is written from
 * `PatientFormValues`, so it round-trips, but it's typed `unknown` on the wire
 * and may be incomplete or — across a schema change — stale, so every field is
 * defensively narrowed and falls back to the empty-form default. Unknown enum
 * values are dropped back to the default rather than poisoning the `<Select>`.
 */
export function draftToFormValues(raw: unknown): PatientFormValues {
  if (raw === null || typeof raw !== "object") return { ...EMPTY_PATIENT_FORM };
  const d = raw as Record<string, unknown>;

  const str = (value: unknown, fallback: string): string =>
    typeof value === "string" ? value : fallback;

  const sbarIn = (d.sbar && typeof d.sbar === "object" ? d.sbar : {}) as Record<string, unknown>;

  const priority = PRIORITIES.includes(d.priority as PatientFormValues["priority"])
    ? (d.priority as PatientFormValues["priority"])
    : EMPTY_PATIENT_FORM.priority;

  const tasks = Array.isArray(d.tasks)
    ? d.tasks.flatMap((row): PatientFormValues["tasks"] => {
        if (!row || typeof row !== "object") return [];
        const t = row as Record<string, unknown>;
        const urgency = TASK_URGENCIES.includes(
          t.urgency as PatientFormValues["tasks"][number]["urgency"],
        )
          ? (t.urgency as PatientFormValues["tasks"][number]["urgency"])
          : "routine";
        return [{ description: str(t.description, ""), urgency }];
      })
    : [];

  return {
    hospitalNumber: str(d.hospitalNumber, ""),
    fullName: str(d.fullName, ""),
    dateOfBirth: str(d.dateOfBirth, ""),
    bedNumber: str(d.bedNumber, ""),
    diagnosis: str(d.diagnosis, ""),
    assignedDoctorId: str(d.assignedDoctorId, ""),
    priority,
    sbar: {
      situation: str(sbarIn.situation, ""),
      background: str(sbarIn.background, ""),
      assessment: str(sbarIn.assessment, ""),
      recommendation: str(sbarIn.recommendation, ""),
    },
    tasks,
    alert: str(d.alert, ""),
  };
}

/**
 * The caller's saved draft for this form, as form values, or `null` if none
 * exists (or the fetch failed — a missing draft and a failed load are equally
 * "just start from the saved/blank state", so both collapse to null).
 */
export async function fetchPatientDraft(
  sessionId: string,
  patientId?: string,
): Promise<PatientFormValues | null> {
  try {
    const result = await sdkGetPatientDraft({
      path: { sessionId },
      query: patientId ? { patientId } : undefined,
    });
    const draft = result.data?.draft;
    if (!draft) return null;
    return draftToFormValues(draft.formData);
  } catch {
    return null;
  }
}

/**
 * Auto-save the current form values as a draft. Returns whether it persisted —
 * the caller uses this only to drive the "draft saved" indicator, never to block
 * the user. A 403 (not the outgoing doctor) or any error is a quiet `false`.
 */
export async function savePatientDraft(
  sessionId: string,
  patientId: string | undefined,
  values: PatientFormValues,
): Promise<boolean> {
  try {
    const result = await sdkSavePatientDraft({
      path: { sessionId },
      body: { formData: values, ...(patientId ? { patientId } : {}) },
    });
    return Boolean(result.data) && !result.error;
  } catch {
    return false;
  }
}

/**
 * Discard the caller's draft after a successful submit, so it never re-restores.
 * Best-effort and idempotent — failures are swallowed (a leftover draft is
 * harmless and will be overwritten on the next edit).
 */
export async function discardPatientDraft(sessionId: string, patientId?: string): Promise<void> {
  try {
    await sdkDiscardPatientDraft({
      path: { sessionId },
      query: patientId ? { patientId } : undefined,
    });
  } catch {
    // Intentionally ignored — see the doc comment.
  }
}
